import {
  Button,
  Divider,
  FileButton,
  Group,
  Paper,
  Anchor,
  Stack,
  Text,
  Tooltip,
} from '@mantine/core';
import {
  IconBuildingBank,
  IconCar,
  IconClock,
  IconClipboardCheck,
  IconId,
  IconMail,
  IconCash,
  IconRefresh,
  IconIdBadge,
  IconFile,
} from '@tabler/icons-react';
import { EtcStateBadge } from './EtcStateBadge';
import { EtcTicketSummary, EtcTicketState } from '../model';
import { LinkText } from '#/components/ui/tickets/components/LinkText.tsx';
import { CopyText } from '#/components/ui/tickets/components/CopyText.tsx';
import { TICKET_TYPE_CONFIG } from '#/components/ui/tickets/config';
import { formatDateTime } from '#/utils/DateUtil';
import {
  useDownloadTransactionReceipt,
  useUploadTransactionReceipt,
} from '../endpoints/ticketsHooks';

type CardFieldRowProps = {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
};

function CardFieldRow({ icon, label, value }: CardFieldRowProps) {
  return (
    <Group gap="xs" wrap="nowrap">
      <Text c="dimmed" size="sm" w={16} style={{ flexShrink: 0 }}>
        {icon}
      </Text>
      <Text size="sm" c="dimmed" w={70} style={{ flexShrink: 0 }}>
        {label}
      </Text>
      <Group gap={4} style={{ flex: 1, justifyContent: 'flex-end' }}>
        {value}
      </Group>
    </Group>
  );
}

type CardProps = EtcTicketSummary & {
  onDetailClick: () => void;
  onConfirmPaymentClick: () => void;
  onFillQueryResultClick: () => void;
};

export function EtcCard({
  id,
  account,
  plateNumber,
  idNumber,
  taxIdNumber,
  state,
  createDateTime,
  updateDateTime,
  hasTransactionReceipt,
  onDetailClick,
  onConfirmPaymentClick,
  onFillQueryResultClick,
}: CardProps) {
  const {
    mutate: uploadTransactionReceipt,
    isPending: isUploadingTransactionReceipt,
  } = useUploadTransactionReceipt();
  const { mutate: downloadTransactionReceipt } =
    useDownloadTransactionReceipt();
  return (
    <Paper withBorder p="md" radius="md" shadow="none">
      <Stack gap="sm">
        {/* header */}
        <Group justify="space-between">
          <Text fw={600}>{TICKET_TYPE_CONFIG['etc-tickets'].name}</Text>
          <Group gap="xs">
            <EtcStateBadge state={state} />
          </Group>
        </Group>

        <Divider />

        {/* fields */}
        <Stack gap={6}>
          <CardFieldRow
            icon={<IconMail size={14} />}
            label="Email"
            value={
              <Tooltip label={account.email}>
                <Text size="sm" lineClamp={1} className="break-all">
                  {account.email}
                </Text>
              </Tooltip>
            }
          />
          <CardFieldRow
            icon={<IconCar size={14} />}
            label="車牌"
            value={<CopyText size="sm">{plateNumber ?? '—'}</CopyText>}
          />
          <CardFieldRow
            icon={<IconIdBadge size={14} />}
            label="證件號碼"
            value={<CopyText size="sm">{idNumber ?? '—'}</CopyText>}
          />
          <CardFieldRow
            icon={<IconId size={14} />}
            label="統一編號"
            value={<CopyText size="sm">{taxIdNumber ?? '—'}</CopyText>}
          />
          <CardFieldRow
            icon={<IconBuildingBank size={14} />}
            label="查繳平台"
            value={
              <LinkText
                size="sm"
                href={TICKET_TYPE_CONFIG['etc-tickets'].getQueryPlatformLink()}
                target="_blank"
              >
                {TICKET_TYPE_CONFIG['etc-tickets'].queryPlatform}
              </LinkText>
            }
          />
        </Stack>

        <Divider />

        {/* timestamps */}
        <Stack gap={6}>
          <CardFieldRow
            icon={<IconClock size={14} />}
            label="建立時間"
            value={
              <Text size="sm">
                {createDateTime ? formatDateTime(createDateTime) : '—'}
              </Text>
            }
          />
          <CardFieldRow
            icon={<IconRefresh size={14} />}
            label="最後更新"
            value={
              <Text size="sm">
                {updateDateTime ? formatDateTime(updateDateTime) : '—'}
              </Text>
            }
          />
        </Stack>

        <Divider />

        {state === EtcTicketState.COMPLETED && (
          <>
            <CardFieldRow
              icon={<IconFile size={14} />}
              label="交易明細"
              value={
                hasTransactionReceipt ? (
                  <Anchor
                    size="sm"
                    component="button"
                    onClick={() => downloadTransactionReceipt(id)}
                  >
                    下載
                  </Anchor>
                ) : (
                  <Group gap={6}>
                    <Text size="sm" c="dimmed">
                      尚未上傳
                    </Text>
                    <Text size="sm" c="dimmed">
                      ·
                    </Text>
                    <FileButton
                      accept="application/pdf,image/jpeg,image/png"
                      disabled={isUploadingTransactionReceipt}
                      onChange={(file) => {
                        if (!file) return;
                        uploadTransactionReceipt({ id, file });
                      }}
                    >
                      {(props) => (
                        <Anchor size="sm" component="button" {...props}>
                          {isUploadingTransactionReceipt ? '上傳中…' : '上傳'}
                        </Anchor>
                      )}
                    </FileButton>
                  </Group>
                )
              }
            />
          </>
        )}

        {/* actions */}
        <Group gap="xs">
          {state === EtcTicketState.CHARGE_SUCCESS && (
            <Button
              flex={'100 0 auto'}
              variant="light"
              size="sm"
              leftSection={<IconClipboardCheck size={14} />}
              onClick={onConfirmPaymentClick}
            >
              確認已代繳
            </Button>
          )}
          {state === EtcTicketState.PENDING && (
            <Button
              flex={'100 0 auto'}
              variant="light"
              size="sm"
              leftSection={<IconCash size={14} />}
              onClick={onFillQueryResultClick}
            >
              回填查詢結果
            </Button>
          )}
          <Button
            flex={'1 1 auto'}
            variant="outline"
            size="sm"
            onClick={onDetailClick}
          >
            查看詳情
          </Button>
        </Group>
      </Stack>
    </Paper>
  );
}
